import type { Brand, Category, CustomFieldDefinition, Item, ItemDeletePreview, ItemPriceSheet, ItemType } from '@erp/shared';
import { Ban, Boxes, Columns3, Eye, EyeOff, FileSpreadsheet, Package, Paperclip, Pencil, Printer, RotateCcw, Star, Trash2, Video, Wrench } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge, Card, Skeleton,
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/shadcn/index.js';
import {
  Button,
  Combobox,
  type ComboboxOption,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { fetchAttachmentObjectUrl, isImageMime, listAttachments } from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { cn } from '../../lib/cn.js';
import { localDateStamp } from '../../lib/csv.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { loadViewPrefs, saveViewPrefs, viewPrefsKey, type SortPref } from '../../lib/viewPrefs.js';
import { downloadXlsx, type XlsxValue } from '../../lib/xlsxExport.js';
import { BulkImportDialog } from '../common/BulkImportDialog.js';
import { FileUploadDialog } from '../common/FileUploadDialog.js';

import { BarcodeFormatDialog } from './BarcodeFormatDialog.js';
import { ItemDetailDialog } from './ItemDetailDialog.js';
import { categoryDescendantIds, flattenCategoryOptions } from './itemHelpers.js';
import { priceSheetTable } from './priceSheetExport.js';
import { printItemBarcode } from './printBarcode.js';

// `barcodes` holds EXTRA scannable codes for the same product (a second
// manufacturer label) — semicolon-separated, because the file is comma-delimited.
const ITEMS_CSV_TEMPLATE = `code,name,description,item_type,unit_of_measure,sale_price,sale_currency_code,purchase_price,purchase_currency_code,tax_rate,is_sellable,is_purchasable,is_inventory_tracked,barcode,barcode_format,barcodes
WIDGET-001,Standard widget,Brown,product,ea,9.99,USD,5.00,USD,0.10,true,true,true,5012345678900,ean13,5012345678917;5012345678924
CONSULT-HR,Consulting hour,,service,hr,150.00,USD,,,0,true,false,false,,,
`;

const CF_FILTER_TYPES = new Set(['select', 'multiselect', 'boolean']);

/** Plain-text rendering of a custom value (for search + sort fallback). */
function cfText(def: CustomFieldDefinition, raw: unknown, yes: string, no: string): string {
  if (raw === undefined || raw === null || raw === '') return '';
  if (def.fieldType === 'boolean') return raw ? yes : no;
  if (def.fieldType === 'multiselect') return Array.isArray(raw) ? raw.join(', ') : String(raw);
  return String(raw);
}

/** Sort key for a custom value — numeric for number fields, else a string. */
function cfSortValue(def: CustomFieldDefinition, raw: unknown): string | number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  if (def.fieldType === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (def.fieldType === 'boolean') return raw ? 1 : 0;
  if (def.fieldType === 'multiselect') return Array.isArray(raw) ? raw.join(', ') : String(raw);
  return String(raw);
}

/** Rich cell for a custom value: badge for booleans, chips for multiselect. */
function cfCell(def: CustomFieldDefinition, raw: unknown, yes: string, no: string): ReactNode {
  if (raw === undefined || raw === null || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
    return <span className="text-fg-subtle">—</span>;
  }
  if (def.fieldType === 'boolean') {
    return <Badge variant={raw ? 'success' : 'secondary'}>{raw ? yes : no}</Badge>;
  }
  if (def.fieldType === 'multiselect' && Array.isArray(raw)) {
    return (
      <span className="flex flex-wrap gap-1">
        {raw.map((v) => (
          <Badge key={String(v)} variant="secondary">
            {String(v)}
          </Badge>
        ))}
      </span>
    );
  }
  return String(raw);
}

/** Apply the active dropdown/boolean custom-field filters to a row. */
function matchesCfFilters(
  it: Item,
  defs: CustomFieldDefinition[],
  filters: Record<string, string>,
): boolean {
  for (const d of defs) {
    const sel = filters[d.code];
    if (!sel || sel === 'all') continue;
    const v = it.customFields?.[d.code];
    if (d.fieldType === 'multiselect') {
      if (!Array.isArray(v) || !v.includes(sel)) return false;
    } else if (d.fieldType === 'boolean') {
      if (Boolean(v) !== (sel === 'true')) return false;
    } else if (String(v ?? '') !== sel) {
      return false;
    }
  }
  return true;
}

// --- persisted UI state (per tenant, localStorage) -------------------------
const colsKey = (t: string | null, u: string | null): string => viewPrefsKey('items-cols', t, u);
const filtersKey = (t: string | null, u: string | null): string => viewPrefsKey('items-filters', t, u);
/** What these were keyed by before they were scoped to a user — read once, see loadViewPrefs. */
const legacyColsKey = (t: string | null): string => `erp-items-cols:${t ?? 'x'}`;
const legacyFiltersKey = (t: string | null): string => `erp-items-filters:${t ?? 'x'}`;
interface SavedFilters {
  typeFilter?: ItemType | 'all';
  statusFilter?: 'all' | 'active' | 'inactive';
  visibilityFilter?: 'all' | 'visible' | 'hidden';
  featuredOnly?: boolean;
  categoryFilter?: string;
  brandFilter?: string;
  cfFilters?: Record<string, string>;
  search?: string;
  /** Whether the on-hand column is on (it costs an extra load — see showStock). */
  showStock?: boolean;
  /** Which column the table is ordered by — as much a part of the view as the filters. */
  sort?: SortPref | null;
}

// Columns hidden by default (opt-in via the Columns menu) so the table stays
// readable; everything else shows unless the user hides it.
const DEFAULT_HIDDEN_COLS = new Set([
  'barcode2', 'barcode3', 'taxRate', 'updated', 'unitsCount', 'variantsCount', 'sellable', 'tracked', 'hidden', 'featured',
]);
// Always shown — not offered in the Columns menu.
const LOCKED_COLS = new Set(['name', 'actions']);

// Extra barcodes beyond the primary one: the item's own alias codes (a second
// manufacturer label on the SAME product) plus its packaging-unit + variant
// barcodes — all already loaded on the DTO. Aliases come first because they are
// the ones that scan to this exact item at this exact price.
// Powers the Barcode 2 / Barcode 3 columns.
function extraBarcodes(it: Item): string[] {
  return [
    ...(it.barcodes ?? []),
    ...(it.units ?? []).map((u) => u.barcode),
    ...(it.variants ?? []).map((v) => v.barcode),
  ].filter((b): b is string => !!b && b.trim().length > 0);
}

// Which delete-preview counts block a hard-delete vs are removed with the item.
const BLOCKING_COUNT_KEYS = [
  'invoiceLines', 'billLines', 'salesOrderLines', 'purchaseOrderLines', 'quotationLines', 'posSaleLines',
  'stockTransferLines', 'stockMovements', 'stocktakeLines',
] as const;
const OWNED_COUNT_KEYS = [
  'units', 'variants', 'batches', 'storeProduct', 'priceListItems', 'reviews', 'storyProducts',
  'couponProducts', 'stockLevels', 'attachments', 'translations',
] as const;
/** "salesOrderLines" → "Sales order lines" fallback when no i18n string exists. */
function humanizeCountKey(key: string): string {
  const s = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ItemsPage(): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canWrite = usePermission('items.write');
  const canFiles = usePermission('attachments.read');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const dir = useLayoutDirection();
  // Per USER as well as per tenant: an office machine is shared, and inheriting
  // a colleague's filters reads as "my products are gone".
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // Filters + column visibility persist per tenant (localStorage) so a refresh
  // keeps the same view. URL ?category/?brand still win when present.
  const saved = useMemo(
    () => loadViewPrefs<SavedFilters>(filtersKey(tenantId, userId), {}, legacyFiltersKey(tenantId)),
    [tenantId, userId],
  );
  const [items, setItems] = useState<Item[] | null>(null);
  // True while the rest of the catalogue is still streaming in (see reload) —
  // an export then would stop at the rows loaded so far.
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPrices, setExportingPrices] = useState(false);
  // Search is SERVER-SIDE (the tenant can have thousands of items; the client only
  // holds a page of them, so a client-only search would miss the rest). Debounced.
  const [search, setSearch] = useState(saved.search ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(saved.search ?? '');
  const [showImport, setShowImport] = useState(false);
  const [showBarcodeFormat, setShowBarcodeFormat] = useState(false);
  const [typeFilter, setTypeFilter] = useState<ItemType | 'all'>(saved.typeFilter ?? 'all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>(saved.statusFilter ?? 'all');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'visible' | 'hidden'>(saved.visibilityFilter ?? 'all');
  const [featuredOnly, setFeaturedOnly] = useState(saved.featuredOnly ?? false);
  /**
   * On-hand quantities are OPT-IN. The list already pages the whole catalogue,
   * and asking every page to aggregate stock as well makes a slow load slower —
   * so the column (and the extra work behind it) only exists once the operator
   * asks for it. The choice is remembered, and turning it OFF costs nothing:
   * the numbers already fetched simply stop being shown.
   */
  const [showStock, setShowStock] = useState(saved.showStock ?? false);
  const [filesItem, setFilesItem] = useState<Item | null>(null);
  const [detailItem, setDetailItem] = useState<Item | null>(null);
  const [thumbVersion, setThumbVersion] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  // Deep-link filters: /inventory/items?category=<id> or ?brand=<id>
  // (used by the count links on the Categories / Brands pages).
  const [categoryFilter, setCategoryFilter] = useState<string>(() => searchParams.get('category') ?? saved.categoryFilter ?? 'all');
  const [brands, setBrands] = useState<Brand[]>([]);
  const [brandFilter, setBrandFilter] = useState<string>(() => searchParams.get('brand') ?? saved.brandFilter ?? 'all');
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [cfFilters, setCfFilters] = useState<Record<string, string>>(saved.cfFilters ?? {});
  // Column visibility (id → shown). Absent id ⇒ default (hidden set aside).
  const [colVis, setColVis] = useState<Record<string, boolean>>(
    () => loadViewPrefs<Record<string, boolean>>(colsKey(tenantId, userId), {}, legacyColsKey(tenantId)),
  );
  // The table's order, held here so it survives leaving the page and coming back.
  const [sort, setSort] = useState<SortPref | null>(saved.sort ?? { id: 'added', dir: 'desc' });
  const isColVisible = (id: string): boolean => colVis[id] ?? !DEFAULT_HIDDEN_COLS.has(id);

  // Persist filters + column visibility whenever they change.
  useEffect(() => {
    saveViewPrefs(filtersKey(tenantId, userId), {
      typeFilter, statusFilter, visibilityFilter, featuredOnly,
      categoryFilter, brandFilter, cfFilters, search, showStock, sort,
    });
  }, [tenantId, userId, typeFilter, statusFilter, visibilityFilter, featuredOnly, categoryFilter, brandFilter, cfFilters, search, showStock, sort]);
  useEffect(() => {
    saveViewPrefs(colsKey(tenantId, userId), colVis);
  }, [tenantId, userId, colVis]);

  // Display names in the active content language (base-column fallback).
  const dataLocale = i18n.language.split('-')[0] ?? 'en';

  // Guards a paged load against a newer one (typing in search) overtaking it.
  const loadSeq = useRef(0);
  // Read inside reload(), which is also called by the search/locale effects.
  const showStockRef = useRef(showStock);
  showStockRef.current = showStock;

  const reload = async (): Promise<void> => {
    // `metrics: 1` asks the API for per-item storefront views + units sold.
    // `sort: newest` returns the most-recently-created first; `q` searches ALL
    // items server-side (code / name / barcode), not just the loaded page.
    // The API caps one request at 500 rows, so page through until the whole
    // catalogue is loaded — the first page renders immediately and the rest
    // stream in, so a 2,500-item tenant sees everything, not just the newest 500.
    const seq = ++loadSeq.current;
    const PAGE = 500;
    const all: Item[] = [];
    setLoadingMore(true);
    try {
      for (let offset = 0; offset < 20_000; offset += PAGE) {
        const r = await api().get<{ items: Item[]; total: number }>('/items', {
          params: {
            locale: dataLocale,
            metrics: 1,
            ...(showStockRef.current ? { stock: 1 } : {}),
            sort: 'newest',
            limit: PAGE,
            offset,
            ...(debouncedSearch ? { q: debouncedSearch } : {}),
          },
        });
        if (seq !== loadSeq.current) return; // superseded by a newer load
        all.push(...r.data.items);
        setItems([...all]);
        if (r.data.items.length < PAGE || all.length >= (r.data.total ?? all.length)) break;
      }
    } finally {
      // A superseded load leaves the flag to the one that overtook it.
      if (seq === loadSeq.current) setLoadingMore(false);
    }
  };

  // Debounce the search box → one request when the user pauses typing.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLocale, debouncedSearch]);

  // Switching the column ON re-loads the catalogue, this time carrying the
  // quantities — but only if what's on screen doesn't already have them (a
  // second toggle after a load costs nothing). Switching it OFF never reloads.
  useEffect(() => {
    if (showStock && items && items.some((it) => it.onHand === undefined)) void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStock]);

  // Categories power the Category column and the filter (localized too).
  useEffect(() => {
    void api()
      .get<{ items: Category[] }>('/categories', { params: { locale: dataLocale } })
      .then((r) => setCategories(r.data.items))
      .catch(() => setCategories([]));
  }, [dataLocale]);

  // Brands power the Brand column and the filter.
  useEffect(() => {
    void api()
      .get<{ items: Brand[] }>('/brands')
      .then((r) => setBrands(r.data.items))
      .catch(() => setBrands([]));
  }, []);

  // Active custom-field definitions power the list columns and filters.
  useEffect(() => {
    void api()
      .get<{ items: CustomFieldDefinition[] }>('/custom-fields')
      .then((r) => setCustomFieldDefs(r.data.items.filter((d) => d.isActive)))
      .catch(() => setCustomFieldDefs([]));
  }, []);

  if (!items) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onDeactivate = async (item: Item): Promise<void> => {
    const message = t('actions.confirmDeactivate', { code: item.code, name: item.name });
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try {
      await api().delete(`/items/${item.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (item: Item): Promise<void> => {
    const message = t('actions.confirmReactivate', { code: item.code, name: item.name });
    if (!(await confirmDialog({ message }))) return;
    try {
      await api().patch(`/items/${item.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  // Hard-delete (permanent). Only offered on DEACTIVATED items. Previews first:
  // blocked (in past documents/stock history) → explain why; else a danger confirm
  // listing exactly what gets removed.
  const onPurge = async (item: Item): Promise<void> => {
    let preview: ItemDeletePreview;
    try {
      preview = (await api().get<ItemDeletePreview>(`/items/${item.id}/delete-preview`)).data;
    } catch {
      toast.error(tc('errors:server'));
      return;
    }
    const counts = preview.counts as unknown as Record<string, number>;
    const rowsFor = (keys: readonly string[]): string[] =>
      keys
        .filter((k) => (counts[k] ?? 0) > 0)
        .map((k) => `• ${t(`delete.counts.${k}`, { defaultValue: humanizeCountKey(k) })}: ${counts[k] ?? 0}`);

    if (preview.blocked) {
      // Referenced by history — deletion is refused server-side. Explain.
      const message = [
        t('delete.blockedIntro', { code: item.code, name: item.name }),
        '',
        ...rowsFor(BLOCKING_COUNT_KEYS),
        '',
        t('delete.blockedHint'),
      ].join('\n');
      await confirmDialog({ title: t('delete.blockedTitle'), message, confirmLabel: tc('actions.ok', { defaultValue: 'OK' }) });
      return;
    }

    const ownedRows = rowsFor(OWNED_COUNT_KEYS);
    const message = [
      t('delete.intro', { code: item.code, name: item.name }),
      '',
      ...(ownedRows.length ? [t('delete.willRemove'), ...ownedRows, ''] : []),
      t('delete.warning'),
    ].join('\n');
    if (!(await confirmDialog({ title: t('delete.title', { name: item.name }), message, tone: 'danger', confirmLabel: t('delete.forever') }))) return;
    try {
      await api().delete(`/items/${item.id}/purge`);
      toast.success(t('delete.done', { name: item.name }));
      await reload();
    } catch (e) {
      const data = (e as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      if (code === 'item_active') toast.error(t('delete.stillActive'));
      else if (code === 'item_referenced') toast.error(t('delete.referenced'));
      else toast.error(tc('errors:server'));
    }
  };

  // One-click storefront/app visibility toggle (items.is_hidden). Optimistic —
  // flip locally right away, revert on failure. The form checkbox on
  // ItemFormPage edits the same flag; this is just the fast path.
  const onToggleHidden = async (item: Item): Promise<void> => {
    const next = !item.isHidden;
    setItems((list) => list?.map((x) => (x.id === item.id ? { ...x, isHidden: next } : x)) ?? list);
    try {
      await api().patch(`/items/${item.id}`, { isHidden: next });
      toast.success(t(next ? 'actions.hiddenToast' : 'actions.shownToast', {
        name: item.name,
        defaultValue: next ? '{{name}} is now hidden from the store & app.' : '{{name}} is now visible in the store & app.',
      }));
    } catch {
      setItems((list) => list?.map((x) => (x.id === item.id ? { ...x, isHidden: item.isHidden } : x)) ?? list);
      toast.error(tc('errors:server'));
    }
  };

  const onPrintBarcode = (item: Item): void => {
    printItemBarcode({
      sku: item.code,
      name: item.name,
      barcode: item.barcode ?? '',
      format: item.barcodeFormat,
      errorLabel: t('actions.printNoBarcode'),
    });
  };

  const categoryNameById = new Map(categories.map((c) => [c.id, c.name] as const));
  const categoryOptions = flattenCategoryOptions(categories);
  // Picking a category means "and everything under it" — the same subtree the
  // server applies to /items?categoryId=, so a deep-link from the Categories
  // page count lands on exactly the rows that were counted.
  const categorySubtree = categoryFilter === 'all' ? null : categoryDescendantIds(categories, categoryFilter);
  // Searchable-dropdown options (type to filter) for the category + brand filters.
  const categoryCombo: ComboboxOption[] = categoryOptions.map((o) => ({ value: o.id, label: o.label }));
  const brandCombo: ComboboxOption[] = brands.map((b) => ({ value: b.id, label: b.title }));
  const catName = (it: Item): string => (it.categoryId ? categoryNameById.get(it.categoryId) ?? '' : '');
  const brandNameById = new Map(brands.map((b) => [b.id, b.title] as const));
  const brandName = (it: Item): string => (it.brandId ? brandNameById.get(it.brandId) ?? '' : '');

  const yesLabel = t('list.yes', { defaultValue: 'Yes' });
  const noLabel = t('list.no', { defaultValue: 'No' });
  const boolCell = (v: boolean): ReactNode =>
    v ? <span className="text-fg">{yesLabel}</span> : <span className="text-fg-subtle">{noLabel}</span>;
  const filterDefs = customFieldDefs.filter((d) => d.filterable && CF_FILTER_TYPES.has(d.fieldType));
  const customColumns: DataTableColumn<Item>[] = customFieldDefs
    .filter((d) => d.showInList)
    .map((d) => ({
      id: `cf_${d.code}`,
      header: d.label,
      sortValue: (it) => cfSortValue(d, it.customFields?.[d.code]),
      searchValue: (it) => cfText(d, it.customFields?.[d.code], yesLabel, noLabel),
      cell: (it) => cfCell(d, it.customFields?.[d.code], yesLabel, noLabel),
    }));

  // Bulk-import template: the static columns plus a cf_<code> column per field.
  const cfHeaders = customFieldDefs.map((d) => `cf_${d.code}`);
  const csvTemplate =
    cfHeaders.length === 0
      ? ITEMS_CSV_TEMPLATE
      : (() => {
          const lines = ITEMS_CSV_TEMPLATE.trimEnd().split('\n');
          const pad = ','.repeat(cfHeaders.length);
          const body = lines.slice(1).map((l) => `${l}${pad}`).join('\n');
          return `${lines[0]},${cfHeaders.join(',')}\n${body}\n`;
        })();

  const columns: DataTableColumn<Item>[] = [
    {
      id: 'code',
      header: t('list.columns.code'),
      sortValue: (it) => it.code,
      searchValue: (it) => it.code,
      cellClassName: 'font-mono text-xs',
      cell: (it) => it.code,
    },
    {
      id: 'name',
      header: t('list.columns.name'),
      sortValue: (it) => it.name,
      searchValue: (it) => it.name,
      cellClassName: 'font-medium',
      cell: (it) => (
        <div className="flex items-center gap-2.5">
          <ItemThumb item={it} version={thumbVersion} enabled={canFiles} />
          <Link
            to={`/inventory/items/${it.id}`}
            className="text-fg underline-offset-4 hover:text-brand hover:underline"
          >
            {it.name}
          </Link>
          <span className="flex items-center gap-1">
            {it.isFeatured && (
              <Star className="h-3.5 w-3.5 text-warning" aria-label={t('create.isFeatured', { defaultValue: 'Featured' })}>
                <title>{t('create.isFeatured', { defaultValue: 'Featured' })}</title>
              </Star>
            )}
            {it.isHidden && (
              <Badge variant="warning" title={t('create.isHidden', { defaultValue: 'Hidden in app' })}>
                <EyeOff className="h-3 w-3" aria-hidden />
                {t('list.hiddenBadge', { defaultValue: 'Hidden' })}
              </Badge>
            )}
            {it.videoUrl && (
              <Video className="h-3.5 w-3.5 text-fg-subtle" aria-label={t('create.videoUrl', { defaultValue: 'Has video' })}>
                <title>{t('create.videoUrl', { defaultValue: 'Has video' })}</title>
              </Video>
            )}
          </span>
        </div>
      ),
    },
    {
      id: 'type',
      header: t('list.columns.type'),
      sortValue: (it) => t(`type.${it.itemType}`),
      searchValue: (it) => t(`type.${it.itemType}`),
      cell: (it) => (
        <Badge variant={it.itemType === 'service' ? 'info' : 'secondary'}>
          {t(`type.${it.itemType}`)}
        </Badge>
      ),
    },
    {
      id: 'category',
      header: t('list.columns.category', { defaultValue: 'Category' }),
      sortValue: catName,
      searchValue: catName,
      cell: (it) => catName(it) || '—',
    },
    {
      id: 'brand',
      header: t('list.columns.brand', { defaultValue: 'Brand' }),
      sortValue: brandName,
      searchValue: brandName,
      cell: (it) => brandName(it) || '—',
    },
    {
      id: 'uom',
      header: t('list.columns.uom'),
      sortValue: (it) => it.unitOfMeasure,
      searchValue: (it) => it.unitOfMeasure,
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (it) => it.unitOfMeasure,
    },
    {
      id: 'salePrice',
      header: t('list.columns.salePrice'),
      align: 'end',
      sortValue: (it) => (it.salePrice ? Number(it.salePrice) : null),
      cellClassName: 'font-mono tabular-nums',
      cell: (it) =>
        it.salePrice && it.saleCurrencyCode
          ? formatMoney(it.salePrice, { locale: i18n.language, currency: it.saleCurrencyCode })
          : '—',
    },
    // No purchase-price column: an item has no single cost — you pay whatever the
    // purchase order / bill says, so the price lives on the purchase document.
    // On hand — only while the toggle is on. A service (or an untracked product)
    // has no shelf to count, so it reads "—" rather than a misleading 0.
    ...(showStock
      ? [{
          id: 'onHand',
          header: t('list.columns.onHand', { defaultValue: 'Qty' }),
          align: 'end' as const,
          sortValue: (it: Item) => (it.isInventoryTracked || Number(it.onHand ?? 0) !== 0 ? Number(it.onHand ?? 0) : null),
          cellClassName: 'font-mono tabular-nums text-xs',
          cell: (it: Item) => {
            if (it.onHand === undefined) return <span className="text-fg-subtle">…</span>;
            const n = Number(it.onHand);
            // An untracked item with nothing on hand has no shelf to report;
            // one that DOES hold stock (thousands here do) reports it.
            if (!it.isInventoryTracked && n === 0) return <span className="text-fg-subtle">—</span>;
            return (
              <span className={n < 0 ? 'font-semibold text-danger' : n === 0 ? 'text-fg-muted' : 'text-fg'}>
                {formatNumber(n, { locale: i18n.language })}
              </span>
            );
          },
        }]
      : []),
    {
      id: 'soldCount',
      header: t('list.columns.sold', { defaultValue: 'Sold' }),
      align: 'end',
      sortValue: (it) => it.soldCount ?? 0,
      cellClassName: 'font-mono tabular-nums text-xs',
      cell: (it) => (it.soldCount != null ? formatNumber(it.soldCount, { locale: i18n.language }) : '—'),
    },
    {
      id: 'viewCount',
      header: t('list.columns.views', { defaultValue: 'Views' }),
      align: 'end',
      sortValue: (it) => it.viewCount ?? 0,
      cellClassName: 'font-mono tabular-nums text-xs text-fg-muted',
      cell: (it) => (it.viewCount != null ? formatNumber(it.viewCount, { locale: i18n.language }) : '—'),
    },
    {
      id: 'barcode',
      header: t('list.columns.barcode'),
      sortValue: (it) => it.barcode ?? '',
      searchValue: (it) => it.barcode ?? '',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (it) => it.barcode ?? '—',
    },
    {
      id: 'barcode2',
      header: t('list.columns.barcode2', { defaultValue: 'Barcode 2' }),
      sortValue: (it) => extraBarcodes(it)[0] ?? '',
      searchValue: (it) => extraBarcodes(it)[0] ?? '',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (it) => extraBarcodes(it)[0] ?? '—',
    },
    {
      id: 'barcode3',
      header: t('list.columns.barcode3', { defaultValue: 'Barcode 3' }),
      sortValue: (it) => extraBarcodes(it)[1] ?? '',
      searchValue: (it) => extraBarcodes(it)[1] ?? '',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (it) => extraBarcodes(it)[1] ?? '—',
    },
    {
      id: 'taxRate',
      header: t('list.columns.taxRate', { defaultValue: 'Tax %' }),
      align: 'end',
      sortValue: (it) => (it.taxRate != null ? Number(it.taxRate) : null),
      cellClassName: 'font-mono tabular-nums text-xs',
      // taxRate is stored as a 0–1 fraction (0.0825 = 8.25%) → ×100 for display.
      cell: (it) => (it.taxRate != null ? `${formatNumber(Number(it.taxRate) * 100, { locale: i18n.language })}%` : '—'),
    },
    {
      id: 'unitsCount',
      header: t('list.columns.unitsCount', { defaultValue: 'Units' }),
      align: 'end',
      sortValue: (it) => it.units?.length ?? 0,
      cellClassName: 'font-mono tabular-nums text-xs text-fg-muted',
      cell: (it) => (it.units?.length ? formatNumber(it.units.length, { locale: i18n.language }) : '—'),
    },
    {
      id: 'variantsCount',
      header: t('list.columns.variantsCount', { defaultValue: 'Variants' }),
      align: 'end',
      sortValue: (it) => it.variants?.length ?? 0,
      cellClassName: 'font-mono tabular-nums text-xs text-fg-muted',
      cell: (it) => (it.variants?.length ? formatNumber(it.variants.length, { locale: i18n.language }) : '—'),
    },
    {
      id: 'sellable',
      header: t('list.columns.sellable', { defaultValue: 'Sellable' }),
      sortValue: (it) => (it.isSellable ? 1 : 0),
      cell: (it) => boolCell(it.isSellable),
    },
    {
      id: 'tracked',
      header: t('list.columns.tracked', { defaultValue: 'Stock tracked' }),
      sortValue: (it) => (it.isInventoryTracked ? 1 : 0),
      cell: (it) => boolCell(it.isInventoryTracked),
    },
    {
      id: 'hidden',
      header: t('list.columns.hidden', { defaultValue: 'Hidden in app' }),
      sortValue: (it) => (it.isHidden ? 1 : 0),
      cell: (it) => boolCell(it.isHidden),
    },
    {
      id: 'featured',
      header: t('list.columns.featured', { defaultValue: 'Featured' }),
      sortValue: (it) => (it.isFeatured ? 1 : 0),
      cell: (it) => boolCell(it.isFeatured),
    },
    {
      id: 'updated',
      header: t('list.columns.updated', { defaultValue: 'Updated' }),
      align: 'end',
      sortValue: (it) => it.updatedAt,
      cellClassName: 'text-xs text-fg-muted whitespace-nowrap',
      cell: (it) => formatDate(it.updatedAt, { locale: i18n.language }),
    },
    {
      id: 'added',
      header: t('list.columns.added', { defaultValue: 'Added' }),
      align: 'end',
      sortValue: (it) => it.createdAt,
      cellClassName: 'text-xs text-fg-muted whitespace-nowrap',
      cell: (it) => formatDate(it.createdAt, { locale: i18n.language }),
    },
    {
      id: 'status',
      header: t('list.columns.status'),
      sortValue: (it) => (it.isActive ? 1 : 0),
      cell: (it) => (
        <Badge variant={it.isActive ? 'success' : 'secondary'}>
          {t(`status.${it.isActive ? 'active' : 'inactive'}`)}
        </Badge>
      ),
    },
    ...customColumns,
    ...[
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-60',
            cell: (it: Item) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDetailItem(it)}
                  aria-label={`${t('detail.view', { defaultValue: 'Quick view' })} ${it.code}`}
                  title={t('detail.view', { defaultValue: 'Quick view' })}
                >
                  <Eye className="h-4 w-4" aria-hidden />
                </Button>
                {canFiles && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilesItem(it)}
                    aria-label={`${t('actions.files', { defaultValue: 'Files' })} ${it.code}`}
                    title={t('actions.files', { defaultValue: 'Files' })}
                  >
                    <Paperclip className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                {canWrite && (
                  // Hide/show on the storefront + customer app — one click, no form.
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onToggleHidden(it)}
                    aria-label={`${t(it.isHidden ? 'actions.show' : 'actions.hide', { defaultValue: it.isHidden ? 'Show in store' : 'Hide from store' })} ${it.code}`}
                    title={t(it.isHidden ? 'actions.show' : 'actions.hide', { defaultValue: it.isHidden ? 'Show in store' : 'Hide from store' })}
                    className={it.isHidden ? 'text-warning hover:text-warning' : undefined}
                  >
                    {it.isHidden
                      ? <Eye className="h-4 w-4" aria-hidden />
                      : <EyeOff className="h-4 w-4" aria-hidden />}
                  </Button>
                )}
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/inventory/items/${it.id}/edit`)}
                    aria-label={`${t('actions.edit')} ${it.code}`}
                    title={t('actions.edit')}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                {canWrite && it.barcode && it.barcodeFormat !== 'qr' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPrintBarcode(it)}
                    aria-label={`${t('actions.print')} ${it.code}`}
                    title={t('actions.print')}
                  >
                    <Printer className="h-4 w-4" aria-hidden />
                  </Button>
                )}
                {canWrite &&
                  (it.isActive ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeactivate(it)}
                      aria-label={`${t('actions.deactivate')} ${it.code}`}
                      title={t('actions.deactivate')}
                    >
                      <Ban className="h-4 w-4" aria-hidden />
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReactivate(it)}
                        aria-label={`${t('actions.reactivate')} ${it.code}`}
                        title={t('actions.reactivate')}
                      >
                        <RotateCcw className="h-4 w-4" aria-hidden />
                      </Button>
                      {/* Remove (permanent) — only on a deactivated item. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void onPurge(it)}
                        aria-label={`${t('delete.action', { defaultValue: 'Remove' })} ${it.code}`}
                        title={t('delete.action', { defaultValue: 'Remove permanently' })}
                        className="text-danger hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </>
                  ))}
              </div>
            ),
          },
        ],
  ];

  // Apply the user's column-visibility choice (locked columns always shown).
  const visibleColumns = columns.filter((c) => LOCKED_COLS.has(c.id) || isColVisible(c.id));
  // Columns the user can toggle in the "Columns" menu (everything but locked ones).
  const hideableColumns = columns.filter((c) => !LOCKED_COLS.has(c.id) && c.id !== 'actions');

  const selectCls =
    'h-8 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted';
  const anyFilter =
    typeFilter !== 'all' ||
    statusFilter !== 'all' ||
    categoryFilter !== 'all' ||
    brandFilter !== 'all' ||
    visibilityFilter !== 'all' ||
    featuredOnly ||
    // Only ACTIVE custom-field defs count — a stale persisted key for a removed
    // field must not light up "Clear filters".
    filterDefs.some((d) => { const v = cfFilters[d.code]; return v && v !== 'all'; });
  const clearFilters = (): void => {
    setTypeFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setBrandFilter('all');
    setVisibilityFilter('all');
    setFeaturedOnly(false);
    setCfFilters({});
  };
  /** The filters in force, in words — an export's info line says what it holds. */
  const activeFilterLabels = (): string[] =>
    [
      typeFilter !== 'all' && t(`type.${typeFilter}`),
      statusFilter !== 'all' && t(`status.${statusFilter}`),
      categoryFilter !== 'all' && `${t('list.columns.category')}: ${categoryNameById.get(categoryFilter) ?? ''}`,
      brandFilter !== 'all' && `${t('list.columns.brand')}: ${brandNameById.get(brandFilter) ?? ''}`,
      visibilityFilter === 'visible' && t('list.visibleInApp', { defaultValue: 'Visible in app' }),
      visibilityFilter === 'hidden' && t('list.hiddenInApp', { defaultValue: 'Hidden in app' }),
      featuredOnly && t('create.isFeatured', { defaultValue: 'Featured' }),
      ...filterDefs.map((d) => {
        const v = cfFilters[d.code];
        if (!v || v === 'all') return false;
        return `${d.label}: ${d.fieldType === 'boolean' ? (v === 'true' ? yesLabel : noLabel) : v}`;
      }),
      debouncedSearch && `“${debouncedSearch}”`,
    ].filter((x): x is string => !!x);

  /**
   * The rows exactly as the table holds them — filters, search and sort, every
   * page — as a styled .xlsx. Quantities go in only when the Quantity column
   * is on (they are not loaded otherwise).
   */
  const exportExcel = async (rows: Item[]): Promise<void> => {
    setExporting(true);
    try {
      const locale = i18n.language;
      const n = (v: number): string => formatNumber(v, { locale });
      const withQty = showStock;
      const qtyOf = (it: Item): number | null => {
        if (it.onHand === undefined) return null;
        const q = Number(it.onHand);
        return !it.isInventoryTracked && q === 0 ? null : q;
      };
      const filterLabels = activeFilterLabels();
      const columns = [
        { header: t('list.columns.code'), kind: 'code' as const },
        { header: t('list.columns.name') },
        { header: t('list.columns.barcode'), kind: 'code' as const },
        { header: t('list.columns.category') },
        { header: t('list.columns.brand') },
        { header: t('list.columns.uom') },
        { header: t('list.columns.salePrice'), kind: 'money' as const, unitPrice: true },
        { header: tc('excel.currency') },
        ...(withQty ? [{ header: t('list.columns.onHand'), kind: 'number' as const }] : []),
        { header: t('list.columns.status') },
      ];
      const totalRow: XlsxValue[] = columns.map(() => null);
      totalRow[0] = tc('excel.totalItems', { n: n(rows.length) });
      if (withQty) totalRow[8] = rows.reduce((sum, it) => sum + (qtyOf(it) ?? 0), 0);
      await downloadXlsx({
        fileName: `items-${localDateStamp()}.xlsx`,
        sheetName: t('title'),
        title: [tenantName, t('title')].filter(Boolean).join(' — '),
        info: [
          tc('excel.exportedAt', { date: formatDateTime(new Date(), { locale }), n: n(rows.length) }),
          filterLabels.length ? tc('excel.filters', { list: filterLabels.join(' · ') }) : '',
        ],
        rtl: dir === 'rtl',
        columns,
        rows: rows.map((it) => {
          const price = it.salePrice && it.saleCurrencyCode
            ? { money: Number(it.salePrice), currency: it.saleCurrencyCode }
            : null;
          return [
            it.code,
            it.name,
            it.barcode,
            catName(it) || null,
            brandName(it) || null,
            it.unitOfMeasure,
            price,
            price ? it.saleCurrencyCode : null,
            ...(withQty ? [qtyOf(it)] : []),
            t(`status.${it.isActive ? 'active' : 'inactive'}`),
          ];
        }),
        totals: [totalRow],
      });
    } catch {
      toast.error(tc('excel.failed'));
    } finally {
      setExporting(false);
    }
  };

  const visibleItems = items.filter(
    (it) =>
      (typeFilter === 'all' || it.itemType === typeFilter) &&
      (statusFilter === 'all' || (statusFilter === 'active' ? it.isActive : !it.isActive)) &&
      (!categorySubtree || (!!it.categoryId && categorySubtree.has(it.categoryId))) &&
      (brandFilter === 'all' || it.brandId === brandFilter) &&
      (visibilityFilter === 'all' || (visibilityFilter === 'hidden' ? it.isHidden : !it.isHidden)) &&
      (!featuredOnly || it.isFeatured) &&
      matchesCfFilters(it, filterDefs, cfFilters),
  );

  /**
   * "Export prices": every product in every unit — the piece, then each pack,
   * one row each — with its barcodes, the normal price, every customer group's
   * price and, for someone who may see costs, the purchase price. Each price
   * in its own currency. With a filter or a search in force it holds just
   * those products (the info line says which); with none, the whole catalogue.
   */
  const filtered = anyFilter || !!debouncedSearch;
  const exportPrices = async (): Promise<void> => {
    setExportingPrices(true);
    try {
      const locale = i18n.language;
      const n = (v: number): string => formatNumber(v, { locale });
      const { data } = await api().get<ItemPriceSheet>('/items/price-sheet', { params: { locale: dataLocale } });
      const keep = filtered ? new Set(visibleItems.map((it) => it.id)) : null;
      const { products, columns, rows, band } = priceSheetTable(
        data,
        {
          code: t('list.columns.code'),
          name: t('list.columns.name'),
          unit: t('priceSheet.unit', { defaultValue: 'Unit' }),
          pieces: t('priceSheet.pieces', { defaultValue: 'Pieces in the unit' }),
          barcode: t('list.columns.barcode'),
          normal: t('profile.prices.normal', { defaultValue: 'Normal price' }),
          purchase: t('priceSheet.cardPurchase', { defaultValue: 'Purchase price (product card)' }),
          lastPurchase: t('priceSheet.lastPurchase', { defaultValue: 'Last purchase price' }),
          qty: t('priceSheet.qty', { defaultValue: 'Quantity' }),
          qtyTotal: t('priceSheet.qtyTotal', { defaultValue: 'Total quantity' }),
          category: t('list.columns.category'),
          brand: t('list.columns.brand'),
          status: t('list.columns.status'),
          store: t('priceSheet.store', { defaultValue: 'Store & app' }),
          otherBarcodes: t('priceSheet.otherBarcodes', { defaultValue: 'Other barcodes' }),
          hiddenUnit: t('priceSheet.hiddenUnit', { defaultValue: 'hidden' }),
          active: t('status.active'),
          inactive: t('status.inactive'),
          visible: t('list.visibleInApp', { defaultValue: 'Visible in app' }),
          hidden: t('list.hiddenInApp', { defaultValue: 'Hidden in app' }),
        },
        { category: (cid) => categoryNameById.get(cid), brand: (bid) => brandNameById.get(bid) },
        keep ? (iid) => keep.has(iid) : undefined,
      );
      if (products === 0) {
        toast.info(t('list.empty'));
        return;
      }
      const totalRow: XlsxValue[] = columns.map(() => null);
      totalRow[0] = tc('excel.totalItems', { n: n(products) });
      await downloadXlsx({
        fileName: `prices-${localDateStamp()}.xlsx`,
        sheetName: t('priceSheet.sheet', { defaultValue: 'Prices' }),
        title: [tenantName, t('priceSheet.title', { defaultValue: 'Prices by unit' })].filter(Boolean).join(' — '),
        info: [
          tc('excel.exportedAt', { date: formatDateTime(new Date(), { locale }), n: n(rows.length) }),
          t('priceSheet.info', {
            defaultValue: '{{products}} products — one row per unit; each price in its column’s currency',
            products: n(products),
          }),
          (data.warehouses?.length ?? 0) > 0
            ? t('priceSheet.qtyNote', { defaultValue: 'Quantities are on hand now, in each row’s unit — cartons on a carton row.' })
            : '',
          filtered ? tc('excel.filters', { list: activeFilterLabels().join(' · ') }) : '',
        ],
        rtl: dir === 'rtl',
        columns,
        rows,
        totals: [totalRow],
        band,
      });
    } catch {
      toast.error(tc('excel.failed'));
    } finally {
      setExportingPrices(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('description')}</p>
        </div>
        <div className="flex gap-2">
          {canWrite && (
            <Button variant="secondary" onClick={() => navigate('/inventory/labels')}>
              {t('labels.print', { defaultValue: 'Print labels' })}
            </Button>
          )}
          {/* Every product × unit × price. Filtered, it waits for the whole
              list to know which products the filters keep. */}
          <Button
            variant="secondary"
            className="gap-1.5"
            onClick={() => void exportPrices()}
            disabled={exportingPrices || (filtered && loadingMore)}
            title={filtered && loadingMore
              ? tc('excel.waitLoading')
              : t('priceSheet.hint', { defaultValue: 'Every product in every unit, with all its prices, as an Excel file' })}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
            {exportingPrices ? tc('excel.exporting') : t('priceSheet.button', { defaultValue: 'Export prices' })}
          </Button>
          {canWrite && (
            <>
              <Button variant="secondary" onClick={() => setShowBarcodeFormat(true)}>
                {t('barcodeBulk.button', { defaultValue: 'Barcode type' })}
              </Button>
              <Button variant="secondary" onClick={() => setShowImport(true)}>
                {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
              </Button>
              <Button onClick={() => navigate('/inventory/items/new')}>{t('create.title')}</Button>
            </>
          )}
        </div>
      </header>

      {showImport && (
        <BulkImportDialog
          title={tc('bulkImport.titleItems', { defaultValue: 'Import items' })}
          endpoint="/items/bulk-import"
          templateCsv={csvTemplate}
          templateName="items-template.csv"
          onDone={() => { setShowImport(false); void reload(); }}
          onCancel={() => setShowImport(false)}
        />
      )}

      {canWrite && (
        <BarcodeFormatDialog
          open={showBarcodeFormat}
          onOpenChange={setShowBarcodeFormat}
          onApplied={() => void reload()}
        />
      )}

      {items.length === 0 && !debouncedSearch ? (
        <EmptyState
          icon={<Package className="h-8 w-8" aria-hidden />}
          title={t('list.empty')}
          description={t('list.emptyDescription')}
          action={
            canWrite ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button onClick={() => navigate('/inventory/items/new')}>{t('create.title')}</Button>
                <Button variant="secondary" onClick={() => setShowImport(true)}>
                  {tc('bulkImport.button', { defaultValue: 'Import CSV' })}
                </Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={visibleColumns}
            data={visibleItems}
            rowKey={(it) => it.id}
            searchable={false}
            sort={sort}
            onSortChange={setSort}
            rowClassName={(it) => (it.isActive ? undefined : 'opacity-60')}
            toolbar={({ rows }) => (
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Server-side search — matches across ALL items, not just this page. */}
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  aria-label={t('searchPlaceholder')}
                  className="h-8 w-60 rounded-full border border-border bg-bg px-3.5 text-xs text-fg outline-none transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/20"
                />
                {/* Columns — show/hide, persisted per tenant. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm" className="h-8 gap-1.5 rounded-full">
                      <Columns3 className="h-4 w-4" aria-hidden />
                      {t('list.columnsMenu', { defaultValue: 'Columns' })}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-80 overflow-auto">
                    <DropdownMenuLabel>{t('list.columnsMenu', { defaultValue: 'Columns' })}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {hideableColumns.map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        checked={isColVisible(col.id)}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={(v) => setColVis((m) => ({ ...m, [col.id]: v }))}
                      >
                        {typeof col.header === 'string' && col.header ? col.header : col.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {/* Exactly what the table shows, every page. Held back while the
                    rest of the catalogue is still arriving. */}
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 gap-1.5 rounded-full"
                  onClick={() => void exportExcel(rows)}
                  disabled={exporting || loadingMore || rows.length === 0}
                  title={loadingMore ? tc('excel.waitLoading') : undefined}
                >
                  <FileSpreadsheet className="h-4 w-4" aria-hidden />
                  {exporting ? tc('excel.exporting') : tc('excel.export')}
                </Button>
                {/* Type — segmented quick filter */}
                <div className="inline-flex rounded-full border border-border p-0.5">
                  {(['all', 'product', 'service'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={typeFilter === f}
                      onClick={() => setTypeFilter(f)}
                      className={cn(
                        'inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition',
                        typeFilter === f ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg',
                      )}
                    >
                      {f === 'all' ? t('list.filterAll', { defaultValue: 'All' }) : t(`type.${f}`)}
                    </button>
                  ))}
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  aria-label={t('list.columns.status')}
                  className={selectCls}
                >
                  <option value="all">{t('list.allStatuses', { defaultValue: 'All statuses' })}</option>
                  <option value="active">{t('status.active')}</option>
                  <option value="inactive">{t('status.inactive')}</option>
                </select>
                {categoryOptions.length > 0 && (
                  <div className="w-48">
                    <Combobox
                      size="sm"
                      options={categoryCombo}
                      value={categoryFilter === 'all' ? undefined : categoryFilter}
                      onChange={(v) => setCategoryFilter(v ?? 'all')}
                      placeholder={t('list.allCategories', { defaultValue: 'All categories' })}
                      ariaLabel={t('list.allCategories', { defaultValue: 'All categories' })}
                    />
                  </div>
                )}
                {brands.length > 0 && (
                  <div className="w-48">
                    <Combobox
                      size="sm"
                      options={brandCombo}
                      value={brandFilter === 'all' ? undefined : brandFilter}
                      onChange={(v) => setBrandFilter(v ?? 'all')}
                      placeholder={t('list.allBrands', { defaultValue: 'All brands' })}
                      ariaLabel={t('list.allBrands', { defaultValue: 'All brands' })}
                    />
                  </div>
                )}
                <select
                  value={visibilityFilter}
                  onChange={(e) => setVisibilityFilter(e.target.value as typeof visibilityFilter)}
                  aria-label={t('list.visibility', { defaultValue: 'Visibility' })}
                  className={selectCls}
                >
                  <option value="all">{t('list.allVisibility', { defaultValue: 'All visibility' })}</option>
                  <option value="visible">{t('list.visibleInApp', { defaultValue: 'Visible in app' })}</option>
                  <option value="hidden">{t('list.hiddenInApp', { defaultValue: 'Hidden in app' })}</option>
                </select>
                {/* Loads (and shows) the on-hand column. Separate from the
                    filters: it changes what is FETCHED, not what is listed. */}
                <button
                  type="button"
                  aria-pressed={showStock}
                  onClick={() => setShowStock((v) => !v)}
                  title={t('list.showStockHint', { defaultValue: 'Loads on-hand quantities — slower on a large catalogue.' })}
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition',
                    showStock
                      ? 'border-transparent bg-brand text-brand-contrast'
                      : 'border-border bg-bg text-fg-muted hover:text-fg',
                  )}
                >
                  <Boxes className="h-3.5 w-3.5" aria-hidden />
                  {t('list.showStock', { defaultValue: 'Quantity' })}
                </button>
                <button
                  type="button"
                  aria-pressed={featuredOnly}
                  onClick={() => setFeaturedOnly((v) => !v)}
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition',
                    featuredOnly
                      ? 'border-transparent bg-brand text-brand-contrast'
                      : 'border-border bg-bg text-fg-muted hover:text-fg',
                  )}
                >
                  <Star className="h-3.5 w-3.5" aria-hidden />
                  {t('create.isFeatured', { defaultValue: 'Featured' })}
                </button>
                {filterDefs.map((d) => (
                  <select
                    key={d.code}
                    value={cfFilters[d.code] ?? 'all'}
                    onChange={(e) => setCfFilters((f) => ({ ...f, [d.code]: e.target.value }))}
                    aria-label={d.label}
                    className={selectCls}
                  >
                    <option value="all">{d.label}</option>
                    {d.fieldType === 'boolean' ? (
                      <>
                        <option value="true">{yesLabel}</option>
                        <option value="false">{noLabel}</option>
                      </>
                    ) : (
                      (d.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))
                    )}
                  </select>
                ))}
                {anyFilter && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs font-medium text-fg-muted underline-offset-4 hover:text-fg hover:underline"
                  >
                    {t('list.clearFilters', { defaultValue: 'Clear filters' })}
                  </button>
                )}
              </div>
            )}
          />
        </Card>
      )}

      <FileUploadDialog
        entityType="item"
        entityId={filesItem?.id ?? ''}
        open={!!filesItem}
        onOpenChange={(o) => {
          if (!o) setFilesItem(null);
        }}
        title={
          filesItem
            ? t('files.title', { defaultValue: 'Files — {{name}}', name: filesItem.name })
            : undefined
        }
        onChange={() => setThumbVersion((v) => v + 1)}
      />

      <ItemDetailDialog
        item={detailItem}
        open={!!detailItem}
        onOpenChange={(o) => { if (!o) setDetailItem(null); }}
        locale={i18n.language}
        canViewImages={canFiles}
        categoryName={detailItem?.categoryId ? categoryNameById.get(detailItem.categoryId) : null}
        brandName={detailItem?.brandId ? brandNameById.get(detailItem.brandId) : null}
      />
    </div>
  );
}

/**
 * 9×9 item thumbnail: the item's first image attachment (auth-fetched object
 * URL), falling back to a type icon. `enabled` gates the network calls to
 * users who can read attachments; `version` forces a refetch after upload.
 */
function ItemThumb({
  item,
  version,
  enabled,
}: {
  item: Item;
  version: number;
  enabled: boolean;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let made: string | null = null;
    void (async () => {
      try {
        const list = await listAttachments('item', item.id);
        const img = list
          .filter((a) => isImageMime(a.mimeType))
          .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img) {
          if (active) setUrl(null);
          return;
        }
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) {
          made = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      } catch {
        if (active) setUrl(null);
      }
    })();
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [item.id, version, enabled]);

  const Icon = item.itemType === 'service' ? Wrench : Package;
  return (
    <div className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-contain" />
      ) : (
        <Icon className="h-[18px] w-[18px] text-fg-muted" aria-hidden />
      )}
    </div>
  );
}
